// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button, Modal, Spinner, TextArea, TextInput, useToast } from '@loa/ui-components';
import { proposalApi } from '../../../../api/proposalApi';
import {
  relationshipCashflowApi,
  type RelationshipCashflow,
  type Scenario,
} from '../../../../api/relationshipCashflowApi';
import type { CashflowImpactSectionContent, ProposalSection } from '../../../../types/proposal';
import { CashflowIncomeChart } from '../../../cashflow/CashflowIncomeChart';
import { useAiBlockDraft } from '../useAiBlockDraft';
import { AiGenerateRow } from './AiGenerateRow';

export type SectionCashflowImpactProps = Readonly<{
  proposalId: string;
  /** The relationship — scenarios and the projection are profile-scoped. */
  profileId?: string;
  section: ProposalSection;
  onSaved: (updated: ProposalSection) => void;
  /**
   * Renders the "Client view" preview (mirrors the Breakthrough Report's
   * Advisor view <-> Client view toggle): the attached scenario's name,
   * chart and caption/narrative are already read-only and render unchanged;
   * the "Add A Cashflow"/"Replace" buttons, the AI-generate row and the
   * caption/narrative inputs are hidden or disabled, and the Save button is
   * hidden. Defaults to false (advisor editable builder).
   */
  readOnly?: boolean;
}>;

function apiErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    return (err.response?.data as { message?: string } | undefined)?.message ?? fallback;
  }
  return fallback;
}

function readContent(section: ProposalSection): CashflowImpactSectionContent {
  return (section.content as CashflowImpactSectionContent | null) ?? {};
}

/**
 * CASHFLOW_IMPACT section (PRD §3.2 row 4, phase P4): attaches one of the
 * relationship's saved cashflow scenarios ("forecasts") to the proposal via
 * `POST /sections/cashflow-impact/scenario`, renders the live projection
 * chart for it (the same engine read the Cashflow workspace uses — the
 * frozen `snapshot` only exists after share and is ignored here), and lets
 * the advisor caption it + write/AI-draft a narrative. Replacing the
 * scenario clears the narrative server-side.
 */
export function SectionCashflowImpact({
  proposalId,
  profileId,
  section,
  onSaved,
  readOnly = false,
}: SectionCashflowImpactProps) {
  const { showToast } = useToast();
  const navigate = useNavigate();

  const content = readContent(section);
  const scenarioId = content.scenarioId;

  const [caption, setCaption] = useState(content.caption ?? '');
  const [narrative, setNarrative] = useState(content.narrative ?? '');
  const [captionDirty, setCaptionDirty] = useState(false);
  const [narrativeDirty, setNarrativeDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const [scenarios, setScenarios] = useState<Scenario[] | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSelection, setPickerSelection] = useState<string | null>(null);
  const [attaching, setAttaching] = useState(false);

  const [cashflow, setCashflow] = useState<RelationshipCashflow | null>(null);
  const [chartLoading, setChartLoading] = useState(false);

  // The saved forecasts list — needed for the picker AND to resolve the
  // attached scenario's display name.
  useEffect(() => {
    if (!profileId) return undefined;
    let cancelled = false;
    relationshipCashflowApi
      .listScenarios(profileId)
      .then((list) => {
        if (!cancelled) setScenarios(list);
      })
      .catch(() => {
        if (!cancelled) setScenarios([]);
      });
    return () => {
      cancelled = true;
    };
  }, [profileId]);

  // Live projection for the attached scenario (never the share-time snapshot).
  useEffect(() => {
    if (!profileId || !scenarioId) {
      setCashflow(null);
      return undefined;
    }
    let cancelled = false;
    setChartLoading(true);
    relationshipCashflowApi
      .getCashflow(profileId, scenarioId)
      .then((cf) => {
        if (!cancelled) setCashflow(cf);
      })
      .catch(() => {
        if (!cancelled) setCashflow(null);
      })
      .finally(() => {
        if (!cancelled) setChartLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [profileId, scenarioId]);

  const attachedScenario = useMemo(
    () => scenarios?.find((s) => s.id === scenarioId) ?? null,
    [scenarios, scenarioId],
  );

  const handleAiApplied = useCallback(
    (updated: ProposalSection) => {
      setNarrative(readContent(updated).narrative ?? '');
      setNarrativeDirty(false);
      onSaved(updated);
      showToast('AI narrative applied', 'success');
    },
    [onSaved, showToast],
  );

  const ai = useAiBlockDraft({
    proposalId,
    profileId,
    sectionKey: 'CASHFLOW_IMPACT',
    block: 'narrative',
    buildMessage: (instruction) =>
      `Draft the narrative block of the cashflow impact section for proposal ${proposalId}, based on cashflow scenario ${scenarioId ?? ''}.${
        instruction ? ` Instruction: ${instruction}` : ''
      }`,
    buildPayload: (parsed) => {
      if (typeof parsed.narrative !== 'string' || parsed.narrative.trim().length === 0) {
        throw new Error('missing narrative');
      }
      return { narrative: parsed.narrative };
    },
    onApplied: handleAiApplied,
  });

  const openPicker = useCallback(() => {
    setPickerSelection(scenarioId ?? null);
    setPickerOpen(true);
  }, [scenarioId]);

  const handleAttach = useCallback(async () => {
    if (!pickerSelection || attaching) return;
    setAttaching(true);
    try {
      const updated = await proposalApi.setCashflowScenario(proposalId, pickerSelection);
      const next = readContent(updated);
      setCaption(next.caption ?? '');
      setNarrative(next.narrative ?? '');
      setCaptionDirty(false);
      setNarrativeDirty(false);
      onSaved(updated);
      setPickerOpen(false);
      showToast('Cashflow scenario attached', 'success');
    } catch (err) {
      showToast(apiErrorMessage(err, 'Failed to attach the cashflow scenario'), 'error');
    } finally {
      setAttaching(false);
    }
  }, [pickerSelection, attaching, proposalId, onSaved, showToast]);

  const handleSave = useCallback(async () => {
    if (saving || !scenarioId) return;
    setSaving(true);
    try {
      let latest = section;
      if (captionDirty) {
        // The caption travels with the scenario attachment endpoint; posting
        // the SAME scenario id only updates the caption (narrative kept).
        latest = await proposalApi.setCashflowScenario(proposalId, scenarioId, caption);
      }
      if (narrativeDirty) {
        // Preserve scenarioId/caption keys — send the latest content merged
        // with the edited narrative.
        latest = await proposalApi.updateSection(proposalId, section.sectionKey, {
          content: { ...(latest.content ?? {}), narrative },
        });
      }
      setCaptionDirty(false);
      setNarrativeDirty(false);
      onSaved(latest);
      showToast('Cashflow Impact saved', 'success');
    } catch (err) {
      showToast(apiErrorMessage(err, 'Failed to save Cashflow Impact'), 'error');
    } finally {
      setSaving(false);
    }
  }, [saving, scenarioId, section, captionDirty, narrativeDirty, proposalId, caption, narrative, onSaved, showToast]);

  const handleOpenWorkspace = useCallback(() => {
    if (profileId) navigate(`/relationships/${profileId}/cashflow`);
  }, [navigate, profileId]);

  const dirty = captionDirty || narrativeDirty;

  return (
    <div className="pension-builder__section" data-testid="section-cashflow-impact">
      {!scenarioId && (
        <div className="pension-builder__placeholder" data-testid="section-cashflow-empty">
          <div>
            <p className="pension-builder__placeholder-text">
              Show the client how this pension changes their long-term cashflow by attaching a
              saved forecast.
            </p>
            {!readOnly && (
              <Button
                variant="filled"
                onClick={openPicker}
                data-testid="section-cashflow-add-btn"
              >
                Add A Cashflow
              </Button>
            )}
          </div>
        </div>
      )}

      {scenarioId && (
        <>
          <div className="pension-builder__cashflow-header">
            <h3 className="pension-builder__facts-card-title" data-testid="section-cashflow-scenario-name">
              {attachedScenario?.name ?? 'Attached cashflow scenario'}
            </h3>
            {!readOnly && (
              <Button
                variant="outlined"
                size="sm"
                onClick={openPicker}
                data-testid="section-cashflow-replace-btn"
              >
                Replace
              </Button>
            )}
          </div>

          {chartLoading && <Spinner />}
          {!chartLoading && cashflow && (
            <CashflowIncomeChart cashflow={cashflow} mode="combined" overrides={attachedScenario?.overrides} />
          )}
          {!chartLoading && !cashflow && (
            <p className="pension-builder__empty" data-testid="section-cashflow-chart-unavailable">
              The projection for this scenario isn't available right now.
            </p>
          )}

          <TextInput
            label="Caption"
            value={caption}
            onChange={(e) => {
              setCaption(e.target.value);
              setCaptionDirty(true);
            }}
            placeholder="A one-line caption shown under the chart…"
            fluid
            disabled={readOnly}
            data-testid="section-cashflow-caption"
          />

          {!readOnly && (
            <AiGenerateRow
              idSuffix="cashflow-narrative"
              instruction={ai.instruction}
              onInstructionChange={ai.setInstruction}
              onGenerate={() => void ai.generate()}
              generating={ai.generating}
              error={ai.error}
            />
          )}
          <TextArea
            label="Narrative"
            value={narrative}
            onChange={(e) => {
              setNarrative(e.target.value);
              setNarrativeDirty(true);
            }}
            placeholder="What this forecast means for the client…"
            rows={6}
            fluid
            disabled={readOnly || ai.generating}
            data-testid="section-cashflow-narrative"
          />

          {!readOnly && (
            <div className="pension-builder__section-footer">
              <Button
                variant="filled"
                onClick={handleSave}
                disabled={saving || !dirty}
                loading={saving}
                data-testid="section-cashflow-save-btn"
              >
                Save
              </Button>
            </div>
          )}
        </>
      )}

      <Modal open={pickerOpen} onClose={() => setPickerOpen(false)} size="medium">
        <Modal.Header title="Attach a cashflow forecast" onClose={() => setPickerOpen(false)} />
        <Modal.Content>
          {scenarios === null && <Spinner />}
          {scenarios !== null && scenarios.length === 0 && (
            <div data-testid="section-cashflow-no-scenarios">
              <p className="pension-builder__empty">
                This relationship has no saved cashflow forecasts yet. Model the new pension in
                the Cashflow workspace first, then attach the forecast here.
              </p>
              <Button
                variant="outlined"
                size="sm"
                onClick={handleOpenWorkspace}
                data-testid="section-cashflow-open-workspace"
              >
                Open Cashflow workspace
              </Button>
            </div>
          )}
          {scenarios !== null && scenarios.length > 0 && (
            <div className="pension-builder__scenario-list" role="listbox" aria-label="Saved cashflow forecasts">
              {scenarios.map((scenario) => (
                <button
                  key={scenario.id}
                  type="button"
                  role="option"
                  aria-selected={pickerSelection === scenario.id}
                  className={
                    pickerSelection === scenario.id
                      ? 'pension-builder__scenario-item pension-builder__scenario-item--selected'
                      : 'pension-builder__scenario-item'
                  }
                  onClick={() => setPickerSelection(scenario.id)}
                  data-testid={`section-cashflow-scenario-option-${scenario.id}`}
                >
                  <span className="pension-builder__scenario-name">{scenario.name}</span>
                  {scenario.description && (
                    <span className="pension-builder__scenario-desc">{scenario.description}</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </Modal.Content>
        {scenarios !== null && scenarios.length > 0 && (
          <Modal.Actions
            secondaryAction={{
              label: 'Cancel',
              onClick: () => setPickerOpen(false),
              disabled: attaching,
            }}
            primaryAction={{
              label: 'Attach forecast',
              onClick: () => {
                void handleAttach();
              },
              disabled: attaching || !pickerSelection,
              loading: attaching,
            }}
          />
        )}
      </Modal>
    </div>
  );
}

export default SectionCashflowImpact;
